import * as React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import clsx from 'clsx';
import { isEqual, isPlainObject } from 'lodash';

import styles from './ParamsPreview.module.css';

export interface ParamsIssue {
  path: string;
  message: string;
}

export interface ParamsPreviewProps {
  params: unknown;
  issues: ParamsIssue[];
  /** Top-level keys that differ from another set of params, e.g. a template's. */
  diff?: ParamsPreviewDiff;
}

export interface ParamsPreviewDiff {
  /** Highlighted in the JSON. */
  changed: string[];
  /** Keys only the other params have; listed, since they have no line to highlight. */
  removed: string[];
  /** What the params are compared with, e.g. "the template". */
  against: string;
}

interface JsonEntry {
  key: string;
  text: string;
}

/**
 * Splits `JSON.stringify(params, null, 2)` into one chunk per top-level key so
 * each can be highlighted. Joining the chunks reproduces the JSON exactly.
 */
const topLevelEntries = (params: unknown): JsonEntry[] | null => {
  if (!isPlainObject(params)) return null;
  const entries = Object.entries(params as Record<string, unknown>).flatMap(([key, value]) => {
    const json = JSON.stringify(value, null, 2);
    if (json === undefined) return [];
    return [{ key, text: `  ${JSON.stringify(key)}: ${json.replace(/\n/g, '\n  ')}` }];
  });
  return entries.length > 0 ? entries : null;
};

/** How long keys changed by the latest edit stay highlighted; matches the fade in the CSS. */
export const RECENT_CHANGE_MS = 5000;

/** Top-level keys sent in `next` whose value differs from `previous`. */
const changedTopLevelKeys = (previous: Record<string, unknown>, next: Record<string, unknown>): string[] =>
  Object.entries(next)
    .filter(([key, value]) => value !== undefined && !isEqual(value, previous[key]))
    .map(([key]) => key);

/** Keys changed by the latest update of `params`, cleared after `RECENT_CHANGE_MS`. */
const useRecentChanges = (params: unknown) => {
  const previous = React.useRef(params);
  const [recent, setRecent] = React.useState<{ keys: string[]; version: number }>({ keys: [], version: 0 });

  React.useEffect(() => {
    const before = previous.current;
    previous.current = params;
    // Nothing to compare on mount or when the report kind first produces params.
    if (before === params || !isPlainObject(before) || !isPlainObject(params)) return;
    const keys = changedTopLevelKeys(before as Record<string, unknown>, params as Record<string, unknown>);
    if (keys.length > 0) setRecent((r) => ({ keys, version: r.version + 1 }));
  }, [params]);

  React.useEffect(() => {
    if (recent.keys.length === 0) return undefined;
    const timer = setTimeout(() => setRecent((r) => ({ ...r, keys: [] })), RECENT_CHANGE_MS);
    return () => clearTimeout(timer);
  }, [recent]);

  return recent;
};

/** Shows the exact JSON that will be submitted and the result of schema validation. */
const ParamsPreview: React.FC<ParamsPreviewProps> = ({ params, issues, diff }) => {
  const changedKeys = diff?.changed;
  const [copied, setCopied] = React.useState(false);
  const json = React.useMemo(() => JSON.stringify(params, null, 2), [params]);
  const entries = React.useMemo(() => topLevelEntries(params), [params]);
  const changed = React.useMemo(() => new Set(changedKeys), [changedKeys]);
  const recentChanges = useRecentChanges(params);
  const recent = React.useMemo(() => new Set(recentChanges.keys), [recentChanges]);
  const showLegend = !!diff || recent.size > 0;

  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box className={styles.preview}>
      {issues.length === 0 ? (
        <Alert severity="success">The report parameters satisfy the report schema.</Alert>
      ) : (
        <Alert severity="error">
          <AlertTitle>
            {issues.length} schema issue{issues.length === 1 ? '' : 's'} must be fixed before submitting
          </AlertTitle>
          <ul className={styles.issues}>
            {issues.map((issue) => (
              <li key={`${issue.path}:${issue.message}`}>
                <code>{issue.path || '(root)'}</code> {issue.message}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <Box className={styles.toolbar}>
        {showLegend && (
          <Box className={styles.legend}>
            {recent.size > 0 && (
              <Typography variant="caption" color="text.secondary" className={styles.legendLine}>
                <span className={clsx(styles.swatch, styles.recentSwatch)} aria-hidden="true" />
                <span>Just changed: {recentChanges.keys.join(', ')}</span>
              </Typography>
            )}
            {diff && (
              <Typography variant="caption" color="text.secondary" className={styles.legendLine}>
                <span className={styles.swatch} aria-hidden="true" />
                <span>
                  {diff.changed.length === 0
                    ? `No changes from ${diff.against}`
                    : `Changed from ${diff.against}: ${diff.changed.join(', ')}`}
                </span>
              </Typography>
            )}
            {diff && diff.removed.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Not sent, but set in {diff.against}: {diff.removed.join(', ')}
              </Typography>
            )}
          </Box>
        )}
        <Button
          size="small"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
      </Box>
      <pre className={styles.json} aria-label="Report parameters JSON">
        {entries ? (
          <code className={styles.lines}>
            {'{\n'}
            {entries.map(({ key, text }, i) => {
              const line = `${text}${i < entries.length - 1 ? ',' : ''}\n`;
              const isChanged = changed.has(key);
              const isRecent = recent.has(key);
              if (!isChanged && !isRecent) {
                return (
                  <span key={key} className={styles.line}>
                    {line}
                  </span>
                );
              }
              const titles = [isRecent && 'Just changed', isChanged && `Changed from ${diff?.against}`];
              return (
                <mark
                  // A new key restarts the fade when the same param changes again.
                  key={isRecent ? `${key}:${recentChanges.version}` : key}
                  className={clsx(isChanged && styles.changed, isRecent && styles.recent)}
                  data-recent={isRecent || undefined}
                  title={titles.filter(Boolean).join('; ')}
                >
                  {line}
                </mark>
              );
            })}
            {'}'}
          </code>
        ) : (
          json
        )}
      </pre>
    </Box>
  );
};

export default ParamsPreview;
